/* istanbul ignore file */
import { FormSection, FormRow, BigFormRow } from 'framework/ui/layout';
import {
  DateInput,
  NumberInput,
  TextInput,
  CountryDropdown,
  EnumDropdown,
  ClearAfterUnmountWrapper,
  BooleanRadiosGroup,
  DurationBetweenTwoDatesInput,
  FormFieldsData,
} from 'framework/form';
import {
  EmployeeContractTypeDropdown,
  EMPLOYEE_CONTRACT_TYPES_ALIASES,
  EMPLOYEE_CONTRACT_ASSIGMENT_ALIAS,
  isPolishOffice,
  OfficeDropdown,
  CONTRACTING_PARTY_FORMS,
} from 'domain/shared/employee';
import {
  SET_PROBATION_END_DATE,
  MIN_PAID_LEAVE_BALANCE,
} from 'domain/people/employee/contracts/shared/employeeContractsService';
import { MomentInput } from 'moment';
import { useAuthorization } from 'framework/authorization';
import useGetAllContracts from './useGetAllContracts';
import { useDateService } from 'framework/utils';
import EmployeeContractPositionDropdown from 'domain/people/employee/contracts/shared/input/EmployeeContractPositionDropdown';
import PaidLeaveBalanceDropdown from '../shared/input/PaidLeaveBalanceDropdown';
import EmployeeContractAssignmentTypeRadios from 'domain/people/employee/contracts/shared/input/EmployeeContractAssignmentTypeRadios';
import { ContractScanUploaderInput } from 'domain/shared/employee';
import { getPreviousContract } from 'domain/people/contracts/shared/contractsService';
import { isBusinessRegistrationCountryVisible } from '../shared/contractFieldVisibility';

const isNotPolishOfficeForm = (formData: FormFieldsData) => {
  return !isPolishOffice(String(formData.officeId));
};

export const isPolishOfficeForm = (formData: FormFieldsData) => {
  return isPolishOffice(String(formData.officeId));
};

const isPolishOfficeAndEmployeeContractType = (formData: FormFieldsData) => {
  return isPolishOffice(String(formData.officeId)) && formData.contractType === EMPLOYEE_CONTRACT_TYPES_ALIASES.EC;
};

const isAllowedIndefiniteContract = (formData: FormFieldsData) => {
  return (
    isPolishOffice(String(formData.officeId)) &&
    formData.contractType !== EMPLOYEE_CONTRACT_TYPES_ALIASES.CFS &&
    formData.contractType !== EMPLOYEE_CONTRACT_TYPES_ALIASES.INTERNSHIP
  );
};

const isExtendedPaidLeaveBalance = (formData: { paidLeaveBalance: number; officeId: any; contractType: string }) => {
  return showPaidLeaveBalanceFieldForEmployeeContract(formData) && formData.paidLeaveBalance > MIN_PAID_LEAVE_BALANCE;
};

const showPaidLeaveBalanceFieldForEmployeeContract = (formData: { officeId: any; contractType: string }) => {
  return isPolishOffice(String(formData.officeId)) && formData.contractType === EMPLOYEE_CONTRACT_TYPES_ALIASES.EC;
};

const showPreviousPaidLeaveBalanceField = (
  currentContract: {
    startDate?: any;
    officeId?: any;
    contractType?: string;
  },
  contracts: any,
  getDaysBetween: {
    (dateTo: MomentInput, dateFrom: MomentInput, format?: string): number;
    (arg0: any, arg1: any): number;
  },
) => {
  const previousContract = getPreviousContract(contracts, currentContract);
  const hasContract = previousContract != null;
  const isContractHasPolishOfficeAndECType = hasContract && isPolishOfficeAndEmployeeContractType(previousContract);
  const isFirstPolishECContract =
    !isContractHasPolishOfficeAndECType && isPolishOfficeAndEmployeeContractType(currentContract);
  const isNewPolishECContractAndNotExtension =
    isContractHasPolishOfficeAndECType &&
    isPolishOfficeAndEmployeeContractType(currentContract) &&
    getDaysBetween(currentContract.startDate, previousContract.endDate) > 1;

  return isFirstPolishECContract || isNewPolishECContractAndNotExtension;
};

type EmployeeContractFormProps = {
  hasAnchorLinks?: boolean;
  isCreateForm?: boolean;
  employeeAlias: string;
};

const EmployeeContractForm = ({
  hasAnchorLinks = false,
  isCreateForm = false,
  employeeAlias,
}: EmployeeContractFormProps) => {
  const { isGranted } = useAuthorization();
  const isSettingProbationEndDateDisabled = isCreateForm || !isGranted(SET_PROBATION_END_DATE);
  const contracts = useGetAllContracts(employeeAlias);
  const { getDaysBetween } = useDateService();

  return (
    <FormSection>
      <FormRow>
        <BooleanRadiosGroup source="secondary" label="Is secondary contract?" />
      </FormRow>
      <FormRow>
        <OfficeDropdown source="officeId" label="Registered office" errorSource="office" />
        <EmployeeContractTypeDropdown source="contractType" label="Contract type" />
        <TextInput source="number" label="Contract number" isVisible={isNotPolishOfficeForm} />
        <DateInput source="dateOfSignature" label="Date of signature" />
        <DateInput source="startDate" label="Contract start date" />
        <BooleanRadiosGroup
          source="isIndefinite"
          label="Is it a contract for an indefinite period?"
          isVisible={isAllowedIndefiniteContract}
        />
        <ClearAfterUnmountWrapper isVisible={contract => !contract.isIndefinite}>
          <DateInput source="endDate" label="Contract end date" />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={contract => !contract.isIndefinite}>
          <DurationBetweenTwoDatesInput dateFromSource="startDate" dateToSource="endDate" label="Contract duration" />
        </ClearAfterUnmountWrapper>
        <EmployeeContractPositionDropdown source="contractPosition" label="Contract position" />
      </FormRow>
      <FormRow>
        <ClearAfterUnmountWrapper
          isVisible={currentContract => showPreviousPaidLeaveBalanceField(currentContract, contracts, getDaysBetween)}
        >
          <NumberInput
            source="previousPaidLeaveBalance"
            label="Start paid leave balance"
            helpText="Paid leave balance for the first calendar year"
            isRequired
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={showPaidLeaveBalanceFieldForEmployeeContract}>
          <PaidLeaveBalanceDropdown
            source="paidLeaveBalance"
            label="Annual paid leave balance"
            helpText="Norm of paid leave balance according to work experience"
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isExtendedPaidLeaveBalance}>
          <DateInput
            source="extendedPaidLeaveBalanceApplyDate"
            label="Extended balance applied from"
            helpText={
              '26 Days balance will be used starting from this date, before that 20 Days balance will be applied.'
            }
          />
        </ClearAfterUnmountWrapper>
      </FormRow>
      <FormRow>
        <EmployeeContractAssignmentTypeRadios source="assignmentType" label="Assignment type" />
        <ClearAfterUnmountWrapper
          isVisible={contract => contract.assignmentType === EMPLOYEE_CONTRACT_ASSIGMENT_ALIAS.PART_TIME}
        >
          <NumberInput source="workRate" label="Work rate, %" />
        </ClearAfterUnmountWrapper>
      </FormRow>
      <FormRow>
        <ClearAfterUnmountWrapper isVisible={contract => contract.contractType === EMPLOYEE_CONTRACT_TYPES_ALIASES.CFS}>
          <EnumDropdown
            source="contractingPartyForm"
            label="Contracting party form"
            options={CONTRACTING_PARTY_FORMS}
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isBusinessRegistrationCountryVisible}>
          <CountryDropdown source="businessRegistrationCountry" label="Business registration country" />
        </ClearAfterUnmountWrapper>
      </FormRow>
      <FormRow>
        <BooleanRadiosGroup
          source="hasProbationPeriod"
          label="Does contract have probation period?"
          isVisible={contract => contract.contractType !== EMPLOYEE_CONTRACT_TYPES_ALIASES.INTERNSHIP}
        />
        <ClearAfterUnmountWrapper
          isVisible={contract =>
            contract.hasProbationPeriod && contract.contractType !== EMPLOYEE_CONTRACT_TYPES_ALIASES.INTERNSHIP
          }
        >
          <DateInput
            disabled={isSettingProbationEndDateDisabled}
            source="probationPeriodEndedAt"
            label="Probation period end date"
            helpText={
              isSettingProbationEndDateDisabled
                ? 'The field will be auto-populated by DaVinci when probation finishes'
                : null
            }
          />
        </ClearAfterUnmountWrapper>
      </FormRow>
      <BigFormRow>
        <ContractScanUploaderInput label="Contract scans" source="scanIds" />
      </BigFormRow>
    </FormSection>
  );
};

export default EmployeeContractForm;
